import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, makeStyles, Typography } from '@material-ui/core';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as FiberManualRecordIcon
} from '@material-ui/icons';
import { isNil } from 'lodash';
import InlineTextPopover from 'components/@common/InlineTextPopover';
import LwtTable from 'components/@common/Table/LwtTable';
import { PrmCurriculumLessonReportResult } from 'types/schema-generated-types';
import { isPrmSetACurriculum } from '../curriculum-util';
import useCommonStyles from '../styles';

type Props = {
  curriculumLessonReport: PrmCurriculumLessonReportResult;
};

const withPercent = (text: any) => text && `${text}%`;
const Title: any = ({ text }: { text?: any }) =>
  text !== 0 && <Typography style={{ fontSize: 13 }}>{text}</Typography>;

const Label = ({ text }: any) => {
  return (
    <Box display="flex">
      <Typography style={{ fontSize: 12, fontWeight: 600 }}>{text}</Typography>
    </Box>
  );
};

type Keys = {
  id?: string | number;
  lessonNumber?: string | number;
  lessonSkill?: string | number;
  metSkill?: any;
  notMetSkill?: string;
  extraSupport?: string;
  averageTime: any;
};

const getColumns = () => {
  return [
    {
      id: 'lessonNumber',
      label: 'LESSON NUMBER',
      sortKey: 'lessonNumber',
      getValue(item: Keys) {
        return (
          <div key={`${item.id}_col_1`}>
            <Title text={item.lessonNumber} />
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'lessonSkill',
      label: 'LESSON SKILL',
      sortKey: 'lessonSkill',
      getValue(item: Keys) {
        return (
          <div key={`${item.id}_col_1`}>
            <Title text={item.lessonSkill} />
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'metSkill',
      label: <Label text="% STUDENTS THAT MET SKILL" />,
      sortKey: 'metSkill',
      getValue(item: Keys) {
        return !isNil(item.metSkill) && item.metSkill !== 0 ? (
          <Box display="flex" key={`${item.id}_col_1`}>
            <Title text={withPercent(item.metSkill)} />
            <CheckCircleIcon
              fontSize="small"
              style={{ color: '#51A486', fontSize: 14, alignSelf: 'center', marginLeft: 4 }}
            />
          </Box>
        ) : (
          '--'
        );
      }
    },
    {
      id: 'notMetSkill',
      label: <Label text="% STUDENTS THAT DID NOT MEET SKILL" />,
      sortKey: 'notMetSkill',
      getValue(item: any) {
        return (
          <Box display="flex" key={`${item.id}_col_1`}>
            {!isNil(item.notMetSkill) && item.notMetSkill !== 0 ? (
              <>
                <Title text={withPercent(item.notMetSkill)} />
                <CancelIcon
                  color="error"
                  fontSize="small"
                  style={{ fontSize: 14, alignSelf: 'center', marginLeft: 4 }}
                />
              </>
            ) : (
              '--'
            )}
          </Box>
        );
      }
    },
    {
      id: 'extraSupport',
      label: <Label text="% STUDENTS THAT NEEDED EXTRA SUPPORT AND MET SKILL" />,
      sortKey: 'extraSupport',
      getValue(item: any) {
        return (
          <Box display="flex" key={`${item.id}_col_1`}>
            {!isNil(item?.extraSupport) && item.extraSupport !== 0 ? (
              <>
                <Title text={withPercent(item.extraSupport)} />
                <FiberManualRecordIcon
                  fontSize="small"
                  style={{ fontSize: 14, alignSelf: 'center', color: '#FFC20E', marginLeft: 4 }}
                />
              </>
            ) : (
              '--'
            )}
          </Box>
        );
      }
    },
    {
      id: 'averageTime',
      label: <Label text="AVERAGE TIME PER LESSON" />,
      sortKey: 'averageTime',
      getValue(item: Keys) {
        return (
          <div key={`${item.id}_col_1`}>
            {item.averageTime !== '0' ? <Title text={`${item.averageTime} minutes`} /> : '--'}
          </div>
        );
      }
    }
  ];
};

const getData = (curriculumLessonReport: PrmCurriculumLessonReportResult): any[] => {
  const result = curriculumLessonReport?.lessons.map(clr => ({
    lessonNumber: clr.lesson?.lessonNumber,
    lessonSkill: clr.lesson?.primarySkillPhonics || clr.lesson?.primarySkillWordStudy,
    extraSupport: clr?.completionStats?.metSkillWithSupportPercentage,
    metSkill: clr?.completionStats?.metSkillPercentage,
    averageTime: `${Math.round(clr?.averageTimeSpentSeconds! / 60)}`,
    notMetSkill: clr?.completionStats?.unmetSkillPercentage
  }));

  return result ?? [];
};

const SkillProgressTable: React.FC<Props> = ({ curriculumLessonReport }) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const { t } = useTranslation('ReportsHelpCommon');

  const title = 'Lesson by Lesson Outline';
  const style = { fontWeight: 450, fontSize: 24, paddingBottom: 15 };

  return (
    <Box className={commonClasses.container}>
      {isPrmSetACurriculum(curriculumLessonReport?.productCurriculum) ? (
        <InlineTextPopover
          PopoverContentProps={{ description: t('prmSetAReporting') }}
          style={style}
        >
          {title}
        </InlineTextPopover>
      ) : (
        <Typography style={style}>Lesson by Lesson Outline</Typography>
      )}
      <LwtTable
        data={getData(curriculumLessonReport)}
        columns={getColumns()}
        classes={{ headRow: () => classes.title }}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  title: { fontSize: 12 }
}));

export default SkillProgressTable;
